// NPM Modules
import Echo from 'laravel-echo';
import { get } from 'svelte/store';
// eslint-disable-next-line no-unused-vars
import Pusher from 'pusher-js';
// Constants
import app_config from '@/config.js';
// Stores
import { jwt } from '@/Stores/Store.js';
import AlertStore from '@/Stores/AlertStore.js';
import MeetingStore from '@/Stores/Racing/MeetingStore.js';
import RaceStore from '@/Stores/Racing/RaceStore.js';
import SelectionStore from '@/Stores/Racing/SelectionStore.js';
import PriceStore from '@/Stores/Racing/PriceStore.js';
import BetTickerStore from '@/Stores/BetTickerStore.js';
import IncomingBetStore from '@/Stores/IncomingBetStore.js';
import SportEventStore from '@/Stores/Sport/SportEventStore.js';
import MarketStore from '@/Stores/Sport/MarketStore.js';
import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';
import CampaignStore from '@/Stores/CampaignStore.js';
import SportcastSelectionStore from '@/Stores/Sport/SportcastSelectionStore.js';
import BetCashoutReviewStore from '@/Stores/BetCashoutReviewStore.js';

let Status = {
	COOLDOWN: 1,
	STOPPED: 2,
	RUNNING: 3
};
let subscriptions = { racing: Status.STOPPED, sport: Status.STOPPED, campaigns: Status.STOPPED };
// we don't unsubscribe immediately in case they come straight back - wait for a cooldown then unsubscribe
const cooldownSec = 10;

const pusher = new Pusher(app_config.PUSHER_KEY, {
	cluster: app_config.PUSHER_CLUSTER,
	forceTLS: true,
	wsHost: app_config.PUSHER_HOST,
	wsPort: app_config.PUSHER_PORT ?? 6001,
	enabledTransports: ['ws', 'wss'],
	disableStats: true,
	channelAuthorization: {
		customHandler: async (params, callback) => {
			const { socketId, channelName } = params;

			try {
				const response = await fetch(`${app_config.API_ENDPOINT}/broadcasting/auth`, {
					method: 'post',
					body: JSON.stringify({
						socket_id: socketId,
						channel_name: channelName
					}),
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
						authorization: 'Bearer ' + get(jwt)
					}
				});
				callback(null, await response.json());
			} catch (error) {
				callback(error, null);
			}
		}
	}
});

const Sockets = {
	init() {
		window.RiskEcho = new Echo({
			broadcaster: 'pusher',
			key: app_config.PUSHER_KEY,
			client: pusher
		});

		/**
		 * Setup any global listeners here
		 */
		function setupGlobalListeners() {
			// BETS
			window.RiskEcho.private('bets')
				.listen('.bet.intercepted', (data) => {
					IncomingBetStore.addBetFromData(data);
					BetTickerStore.addBetFromData(data);
				})
				.listen('.bet.accepted', (data) => {
					IncomingBetStore.removeBet(data.id);
					BetTickerStore.mergeBetData(data);
				})
				.listen('.bet.rejected', (data) => {
					IncomingBetStore.removeBet(data.id);
					BetTickerStore.mergeBetData(data);
				})
				.listen('.bet.placed', (data) => {
					BetTickerStore.addBetFromData(data);
				})
				.listen('.bet.cashed_out', (data) => {
					BetTickerStore.addBetFromData(data);
				})
				.listen('.bet.review_cashout', (data) => {
					BetCashoutReviewStore.addBetFromData(data);
				});

			// ALERTS
			window.RiskEcho.private('alerts')
				.listen('.created', (data) => {
					AlertStore.addAlertFromData(data);
				})
				.listen('.deleted', (data) => {
					if (!data.data) {
						return;
					}
					AlertStore.removeAlert(data.data.id);
				})
				.listen('.updated', (data) => {
					if (!data.id) {
						return;
					}
					AlertStore.updateAlert(data);
				});
		}

		/**
		 * Setup any listeners for the user here
		 * @param {*} data
		 */
		//  function setupUserListeners(data) {
		//      RiskEcho.private(`user.${data.id}`)
		//          .listen(".wallet.balance.updated", e => {
		//              console.log({ e })
		//          });
		//  }

		/**
		 * We can only set up listeners once we have access to the user store.
		 * This will wait for user store, then no longer listen for store updates.
		 */
		// const unsubscribe = AuthUserStore.subscribe(data => {
		//     if (data) {
		//         setupUserListeners(data);
		//         unsubscribe();
		//     }
		// });

		/**
		 * Keep our Echo `auth.header` token updated with the app JWT
		 */
		jwt.subscribe((value) => {
			if (value) {
				setupGlobalListeners();
			}
		});
	},

	subscribeToRacing() {
		if (app_config.RACING_ENABLED && subscriptions.racing === Status.STOPPED) {
			window.RiskEcho.private('races')
				// TODO: .updated will be deprecated in future API update and use .batch.updated instead
				.listen('.updated', (data) => {
					RaceStore.applyRaceUpdates(data);
				})
				.listen('.batch.updated', (data) => {
					RaceStore.applyBatchRaceUpdates(data.races);
				});

			window.RiskEcho.private('meetings').listen('.updated', (data) => {
				MeetingStore.applyMeetingUpdates(data);
			});

			window.RiskEcho.private('racing_selections')
				// TODO: .updated will be deprecated in future API update and use .batch.updated instead
				.listen('.updated', (data) => {
					SelectionStore.applySelectionUpdates(data);
				})
				.listen('.batch.updated', (data) => {
					if ('racing_selections' in data) {
						SelectionStore.applyBatchSelectionUpdates(data.racing_selections);
					}
				})
				.listen('.prices.updated', (data) => {
					PriceStore.applyPriceUpdates(data);
				});
		}
		subscriptions.racing = Status.RUNNING;
	},

	unsubscribeFromRacing() {
		subscriptions.racing = Status.COOLDOWN;
		setTimeout(() => {
			if (subscriptions.racing === Status.COOLDOWN) {
				if (app_config.RACING_ENABLED) {
					window.RiskEcho.leave('races');
					window.RiskEcho.leave('meetings');
					window.RiskEcho.leave('racing_selections');
				}
				subscriptions.racing = Status.STOPPED;
			}
		}, cooldownSec * 1000);
	},

	subscribeToSport() {
		if (app_config.SPORT_ENABLED && subscriptions.sport === Status.STOPPED) {
			window.RiskEcho.private('sport_events').listen('.updated', (data) => {
				SportEventStore.applySportEventUpdates(data);
			});
			window.RiskEcho.private('sport_markets').listen('.updated', (data) => {
				MarketStore.applyMarketUpdates(data);
			});
			window.RiskEcho.private('sport_selections').listen('.sport_selections.updates', (data) => {
				// add to pending so updates are debounced
				SportSelectionStore.addToPending(data.sport_selections);
			});
			window.RiskEcho.private('sportcast_selections').listen('.sportcast_selections.updates', (data) => {
				// add to pending so updates are debounced
				SportcastSelectionStore.addToPending(data.sportcast_selections);
			});
		}
		subscriptions.sport = Status.RUNNING;
	},

	unsubscribeFromSport() {
		subscriptions.sport = Status.COOLDOWN;
		setTimeout(() => {
			if (subscriptions.sport === Status.COOLDOWN) {
				if (app_config.SPORT_ENABLED) {
					window.RiskEcho.leave('sport_events');
					window.RiskEcho.leave('sport_markets');
					window.RiskEcho.leave('sport_selections');
					window.RiskEcho.leave('sportcast_selections');
				}
				subscriptions.sport = Status.STOPPED;
			}
		}, cooldownSec * 1000);
	},

	subscribeToCampaigns() {
		if (subscriptions.campaigns === Status.STOPPED) {
			window.RiskEcho.private('campaigns')
				.listen('.updated', (data) => {
					CampaignStore.applyCampaignUpdates(data);
				})
				.listen('.created', (data) => {
					CampaignStore.addCampaignFromData(data);
				});
		}
		subscriptions.campaigns = Status.RUNNING;
	},

	unsubscribeFromCampaigns() {
		subscriptions.campaigns = Status.COOLDOWN;
		setTimeout(() => {
			if (subscriptions.campaigns === Status.COOLDOWN) {
				window.RiskEcho.leave('campaigns');
				subscriptions.racing = Status.STOPPED;
			}
		}, cooldownSec * 1000);
	}
};

if (app_config.ENABLE_SOCKETS) Sockets.init();

export default Sockets;
